'use client';

import {
  layoutNewsletterDescription,
  layoutNewsletterEmail,
  layoutNewsletterPlaceholder,
  layoutNewsletterSubmit,
  layoutNewsletterTitle,
  newsletterOptinDescription,
  newsletterOptinTitle,
} from '@bells/i18n';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/alert-dialog';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../components/form';
import { Input } from '../../../components/input';
import { LoadingButton } from '../../../components/loading-button';

export const SubscribeSchema = z.object({ email: z.string().min(1).email() });
export type Subscribe = z.infer<typeof SubscribeSchema>;

export default function NewsletterComponent() {
  const [loading] = useState<boolean>();
  const [open, setOpen] = useState<boolean>();
  const form = useForm<Subscribe>({
    resolver: zodResolver(SubscribeSchema),
  });

  const onSubmit = async (data: Subscribe) => {
    let formData = new FormData();
    formData.append('email', data.email);
    formData.append('l', '9eb6a3bf-9c30-4a88-af1f-9d0d031cabf2');

    const result = await fetch(
      'https://cdn-mail.bb-escort.de/api/public/subscription',
      {
        method: 'POST',
        body: formData,
      }
    );

    if (result.status == 200) {
      form.reset();
      setOpen(true);
    } else {
      const data = (await result.json()) as { message: string };
      form.setError('email', { type: '', message: data.message });
    }
  };

  return (
    <div className='w-full'>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{newsletterOptinTitle()}</AlertDialogTitle>
            <AlertDialogDescription>
              {newsletterOptinDescription()}
            </AlertDialogDescription>
          </AlertDialogHeader>
        </AlertDialogContent>
      </AlertDialog>

      <div className='grid grid-cols-6 gap-4 rounded-xl border p-6 shadow lg:p-8 xl:p-12'>
        <div className='col-span-6 grid w-full gap-4 text-foreground lg:col-span-4'>
          <div className='col-span-4 flex flex-col gap-4'>
            <h5 className='text-xl font-medium'>{layoutNewsletterTitle()}</h5>
            <p className='max-w-[500px] text-base text-muted-foreground'>
              {layoutNewsletterDescription()}
            </p>
          </div>

          <div className='col-span-2'>
            <FormProvider {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <FormField
                  control={form.control}
                  name={`email`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{layoutNewsletterEmail()}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ''}
                          placeholder={layoutNewsletterPlaceholder()}
                          className='w-full'
                        ></Input>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <LoadingButton
                  type='submit'
                  className='mt-4'
                  loading={form.formState.isSubmitting || loading}
                  disabled={form.formState.isSubmitting || loading}
                >
                  {layoutNewsletterSubmit()}
                </LoadingButton>
              </form>
            </FormProvider>
          </div>
        </div>

        <div className='relative col-span-2 hidden h-full w-full items-center lg:flex'>
          <div className='absolute flex h-full w-full items-center justify-end overflow-hidden'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              className='h-full'
              viewBox='0 0 808.84067 681.8936'
            >
              <path
                d='M866.24553,659.31387,597.90045,536.80922,736.07526,234.13885l268.34507,122.50466Z'
                transform='translate(-195.57967 -109.0532)'
                fill='#f2f2f2'
              ></path>
              <path
                d='M907.12341,483.47827l-203.14462-92.7394a1.58594,1.58594,0,0,1,1.31725-2.88543l203.14462,92.7394a1.58594,1.58594,0,0,1-1.31725,2.88543Z'
                transform='translate(-195.57967 -109.0532)'
                fill='#fff'
              ></path>
              <path
                d='M918.41962,458.73405,715.275,365.99465a1.58594,1.58594,0,1,1,1.31725-2.88543l203.14462,92.7394a1.58594,1.58594,0,1,1-1.31725,2.88543Z'
                transform='translate(-195.57967 -109.0532)'
                fill='#fff'
              ></path>
              <path
                d='M929.71583,433.98984,726.57121,341.25043a1.58594,1.58594,0,1,1,1.31725-2.88543l203.14462,92.7394a1.58595,1.58595,0,0,1-1.31725,2.88544Z'
                transform='translate(-195.57967 -109.0532)'
                fill='#fff'
              ></path>
              <path
                d='M941.012,409.24562,737.86742,316.50621a1.58594,1.58594,0,1,1,1.31725-2.88543l203.14462,92.73941a1.58594,1.58594,0,0,1-1.31725,2.88543Z'
                transform='translate(-195.57967 -109.0532)'
                fill='#fff'
              ></path>
              <path
                d='M896.91469,505.84036,693.77007,413.101a1.58594,1.58594,0,1,1,1.31725-2.88543l203.14462,92.7394a1.58594,1.58594,0,0,1-1.31725,2.88543Z'
                transform='translate(-195.57967 -109.0532)'
                fill='#fff'
              ></path>
              <path
                d='M930.83742,372.9484,772.15573,300.50717a3.9649,3.9649,0,0,1-2.22982-4.77891,3.816,3.816,0,0,1,5.20033-2.25249L933.75967,365.895a4.00065,4.00065,0,0,1,2.261,4.85011A3.81579,3.81579,0,0,1,930.83742,372.9484Z'
                transform='translate(-195.57967 -109.0532)'
                fill='#fff'
              ></path>
              <path
                d='M320.27065,356.64351,588.61572,234.13885,726.79053,536.80922,458.44546,659.31387Z'
                transform='translate(-195.57967 -109.0532)'
                fill='#f2f2f2'
              ></path>
              <path
                d='M417.56758,483.47827l203.14461-92.7394a1.58594,1.58594,0,1,0-1.31725-2.88543l-203.14462,92.7394a1.58594,1.58594,0,1,0,1.31726,2.88543Z'
                transform='translate(-195.57967 -109.0532)'
                fill='#fff'
              ></path>
              <path
                d='M406.27137,458.73405,609.416,365.99465a1.58594,1.58594,0,0,0-1.31726-2.88543l-203.14462,92.7394a1.58594,1.58594,0,1,0,1.31726,2.88543Z'
                transform='translate(-195.57967 -109.0532)'
                fill='#fff'
              ></path>
              <path
                d='M394.97516,433.98984l203.14462-92.73941a1.58594,1.58594,0,0,0-1.31726-2.88543L393.6579,431.1044a1.58595,1.58595,0,0,0,1.31726,2.88544Z'
                transform='translate(-195.57967 -109.0532)'
                fill='#fff'
              ></path>
              <path
                d='M383.679,409.24562l203.14462-92.73941a1.58594,1.58594,0,1,0-1.31726-2.88543L382.36169,406.36019a1.58594,1.58594,0,0,0,1.31726,2.88543Z'
                transform='translate(-195.57967 -109.0532)'
                fill='#fff'
              ></path>
              <path
                d='M427.7763,505.84036,630.92092,413.101a1.58594,1.58594,0,0,0-1.31726-2.88543L426.459,502.95493a1.58594,1.58594,0,1,0,1.31726,2.88543Z'
                transform='translate(-195.57967 -109.0532)'
                fill='#fff'
              ></path>
              <path
                d='M393.85356,372.9484l158.68169-72.44123a3.96488,3.96488,0,0,0,2.22982-4.77891,3.816,3.816,0,0,0-5.20032-2.25249L390.93131,365.895a4.00067,4.00067,0,0,0-2.261,4.85011A3.81579,3.81579,0,0,0,393.85356,372.9484Z'
                transform='translate(-195.57967 -109.0532)'
                fill='#fff'
              ></path>
              <path
                d='M861.997,661.85822H460.74089V209.2756H861.997Z'
                transform='translate(-195.57967 -109.0532)'
                fill='#fff'
              ></path>
              <path
                d='M861.997,661.85822H460.74089V209.2756H861.997Zm-399.09877-2.15729H859.83966v-448.268H462.89818Z'
                transform='translate(-195.57967 -109.0532)'
                fill='#cacaca'
              ></path>
              <path
                d='M813.24987,421.18544H509.488a2.15729,2.15729,0,1,1,0-4.31458h303.7619a2.15729,2.15729,0,0,1,0,4.31458Z'
                transform='translate(-195.57967 -109.0532)'
                fill='#e4e4e4'
              ></path>
              <path
                d='M813.24987,384.18544H509.488a2.15729,2.15729,0,1,1,0-4.31458h303.7619a2.15729,2.15729,0,0,1,0,4.31458Z'
                transform='translate(-195.57967 -109.0532)'
                fill='#e4e4e4'
              ></path>
              <path
                d='M813.24987,347.18544H509.488a2.15729,2.15729,0,1,1,0-4.31458h303.7619a2.15729,2.15729,0,0,1,0,4.31458Z'
                transform='translate(-195.57967 -109.0532)'
                fill='#e4e4e4'
              ></path>
              <path
                d='M813.24987,310.18544H509.488a2.15729,2.15729,0,1,1,0-4.31458h303.7619a2.15729,2.15729,0,0,1,0,4.31458Z'
                transform='translate(-195.57967 -109.0532)'
                fill='#e4e4e4'
              ></path>
              <path
                d='M813.24987,454.62344H509.488a2.15729,2.15729,0,1,1,0-4.31458h303.7619a2.15729,2.15729,0,0,1,0,4.31458Z'
                transform='translate(-195.57967 -109.0532)'
                fill='#e4e4e4'
              ></path>
              <path
                d='M813.24987,620.62344H509.488a2.15729,2.15729,0,1,1,0-4.31458h303.7619a2.15729,2.15729,0,0,1,0,4.31458Z'
                transform='translate(-195.57967 -109.0532)'
                fill='#e4e4e4'
              ></path>
              <path
                d='M740.48441,585.9937h-158.231a3.99339,3.99339,0,0,1-3.989-3.989V488.92762a3.99338,3.99338,0,0,1,3.989-3.989h158.231a3.99337,3.99337,0,0,1,3.989,3.989v93.07706A3.99338,3.99338,0,0,1,740.48441,585.9937Z'
                transform='translate(-195.57967 -109.0532)'
                fill='#fff'
              ></path>
              <path
                d='M687.64325,532.82035a8.8014,8.8014,0,0,0-12.64552-.226L630.46735,577.125a6.28787,6.28787,0,0,1-8.86881.01331l-6.59513-6.58189a8.78014,8.78014,0,0,0-12.41958,0L589.8195,583.33459H732.91833v-1.82165Z'
                transform='translate(-195.57967 -109.0532)'
                fill='hsl(var(--brand))'
              ></path>
              <path
                d='M740.48441,585.9937h-158.231a3.99339,3.99339,0,0,1-3.989-3.989V488.92762a3.99338,3.99338,0,0,1,3.989-3.989h158.231a3.99337,3.99337,0,0,1,3.989,3.989v93.07706A3.99338,3.99338,0,0,1,740.48441,585.9937ZM582.25342,487.598a1.3312,1.3312,0,0,0-1.32967,1.32967v93.07706a1.33119,1.33119,0,0,0,1.32967,1.32967h158.231a1.33119,1.33119,0,0,0,1.32968-1.32967V488.92762a1.3312,1.3312,0,0,0-1.32968-1.32967Z'
                transform='translate(-195.57967 -109.0532)'
                fill='#3f3d56'
              ></path>
              <path
                d='M359.01738,570.43562a10.05575,10.05575,0,0,0-8.67173-12.74978l-8.29088-34.75975-12.7075,13.5408,9.82137,30.9325a10.11027,10.11027,0,0,0,19.84874,3.03623Z'
                transform='translate(-195.57967 -109.0532)'
                fill='#ffb6b6'
              ></path>
              <path
                d='M336.85563,557.82806a4.505,4.505,0,0,1-2.70763-2.47619l-17.579-42.99751a46.37343,46.37343,0,0,1-1.64817-32.60257l11.264-35.28292a14.49652,14.49652,0,1,1,28.87239,2.642l-14.93809,56.81578,10.00513,44.62172a4.51466,4.51466,0,0,1-2.20041,4.67919l-7.41611,4.23741a4.50564,4.50564,0,0,1-2.62316.57567A4.45469,4.45469,0,0,1,336.85563,557.82806Z'
                transform='translate(-195.57967 -109.0532)'
                fill='#e4e4e4'
              ></path>
              <polygon
                points='203.799 669.082 216.058 669.082 221.891 621.794 203.796 621.794 203.799 669.082'
                fill='#ffb6b6'
              ></polygon>
              <path
                d='M396.25093,774.13262l24.144-.001h.001a15.3873,15.3873,0,0,1,15.38647,15.38623v.5l-39.53076.00146Z'
                transform='translate(-195.57967 -109.0532)'
                fill='hsl(var(--main))'
              ></path>
              <polygon
                points='134.799 669.082 147.058 669.082 152.891 621.794 134.796 621.794 134.799 669.082'
                fill='#ffb6b6'
              ></polygon>
              <path
                d='M327.25093,774.13262l24.144-.001h.001a15.3873,15.3873,0,0,1,15.38647,15.38623v.5l-39.53076.00146Z'
                transform='translate(-195.57967 -109.0532)'
                fill='hsl(var(--main))'
              ></path>
              <path
                d='M406.76052,539.61811l-71.89678-6.32584s-7.10322,45.32584-6.10322,63.32584,2,46,2,46l-8,122,31-4,4-101,13-55,15,70,10,90,27-3-9-109S421.76049,554.61811,406.76052,539.61811Z'
                transform='translate(-195.57967 -109.0532)'
                fill='hsl(var(--main))'
              ></path>
              <circle
                cx='182.18086'
                cy='277.56493'
                r='20'
                fill='#ffb8b8'
              ></circle>
              <path
                d='M399.66234,431.2756l-14.28451-11.3709s-15.469-11.93613-24.54315-3.11136l-4.07412,5.82477s-23.09822,8.65749-23.09822,19.65749l1.09822,60.34251s-8,31,4,33l69.90178,5.65749-.90178-57.65749Z'
                transform='translate(-195.57967 -109.0532)'
                fill='#e4e4e4'
              ></path>
              <path
                d='M362.26052,404.11811s-21-15-10-33c7.63117-12.48737,23.44413-11.49893,32.414-9.72226a15.154,15.154,0,0,1,11.086,8.72226c1.5,3.5,1.5,7-4.5,7-12,0-6,9-6,9s-8,2-6,9S362.26052,404.11811,362.26052,404.11811Z'
                transform='translate(-195.57967 -109.0532)'
                fill='hsl(var(--main))'
              ></path>
              <path
                d='M471.81865,536.66284a10.05576,10.05576,0,0,0-13.94451-6.58077L433.082,504.34635l-4.072,18.11774,24.16736,21.66143a10.11027,10.11027,0,0,0,18.64135-7.46268Z'
                transform='translate(-195.57967 -109.0532)'
                fill='#ffb6b6'
              ></path>
              <path
                d='M446.32471,537.05427a4.505,4.505,0,0,1-3.58993-.75841l-36.97666-28.11642a46.37345,46.37345,0,0,1-17.97415-27.2502l-8.21132-36.11559a14.49652,14.49652,0,1,1,26.215-12.38415l15.97959,56.5317,31.27651,33.36137a4.51469,4.51469,0,0,1,.48026,5.1484l-4.2374,7.41612a4.50564,4.50564,0,0,1-1.96754,1.82787A4.45492,4.45492,0,0,1,446.32471,537.05427Z'
                transform='translate(-195.57967 -109.0532)'
                fill='#e4e4e4'
              ></path>
              <path
                d='M954.23551,139.23028H376.67973a.83826.83826,0,0,1,0-1.67651H954.23551a.83826.83826,0,0,1,0,1.67651Z'
                transform='translate(-195.57967 -109.0532)'
                fill='#cacaca'
              ></path>
              <circle
                cx='199.96734'
                cy='9.22077'
                r='9.22077'
                fill='#3f3d56'
              ></circle>
              <circle
                cx='231.82092'
                cy='9.22077'
                r='9.22077'
                fill='#3f3d56'
              ></circle>
              <circle
                cx='263.67451'
                cy='9.22077'
                r='9.22077'
                fill='#3f3d56'
              ></circle>
              <path
                d='M935.15042,114.69325H912.51761a1.67651,1.67651,0,0,1,0-3.353h22.63281a1.67651,1.67651,0,0,1,0,3.353Z'
                transform='translate(-195.57967 -109.0532)'
                fill='#3f3d56'
              ></path>
              <path
                d='M935.15042,120.98014H912.51761a1.67651,1.67651,0,0,1,0-3.353h22.63281a1.67651,1.67651,0,0,1,0,3.353Z'
                transform='translate(-195.57967 -109.0532)'
                fill='#3f3d56'
              ></path>
              <path
                d='M935.15042,127.267H912.51761a1.67651,1.67651,0,0,1,0-3.353h22.63281a1.67651,1.67651,0,0,1,0,3.353Z'
                transform='translate(-195.57967 -109.0532)'
                fill='#3f3d56'
              ></path>
              <path
                d='M780.15534,271.01854H542.8788a5.39326,5.39326,0,0,1-5.45881-4.65385,5.19076,5.19076,0,0,1,5.1625-5.72493H779.78687a5.44194,5.44194,0,0,1,5.53758,4.72437A5.19046,5.19046,0,0,1,780.15534,271.01854Z'
                transform='translate(-195.57967 -109.0532)'
                fill='hsl(var(--brand))'
              ></path>
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}
